/**
 * Gender Report page — boys / girls / other per hostel.
 *
 * GET /api/admin/reports/gender → {rows: [{hostel_id, hostel_name, boys, girls, other, total, total_beds}], totals: {...}}
 *
 * Visible to super_admin (all hostels) + hostel_admin (their hostel only).
 */
import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Users, GenderMale, GenderFemale, Question } from "@phosphor-icons/react";

const OCC_HIGH_CLS = "border-[var(--vs-danger)]/40 text-[var(--vs-danger)]";
const OCC_MED_CLS = "border-[var(--vs-warning)]/40 text-[var(--vs-warning)]";
const OCC_LOW_CLS = "border-[var(--vs-success)]/40 text-[var(--vs-success)]";

function occupancyBadgeClass(pct) {
  if (pct >= 90) return OCC_HIGH_CLS;
  if (pct >= 70) return OCC_MED_CLS;
  return OCC_LOW_CLS;
}

function GenderCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className={`vs-card p-4 border-l-4 ${accent}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)] mb-1">
        <Icon size={12} weight="duotone" /> {label}
      </div>
      <div className="font-display text-3xl">{value}</div>
    </div>
  );
}

export default function GenderReport() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/reports/gender").then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: { boys: 0, girls: 0, other: 0, total: 0 } }));
  }, []);

  if (!data) {
    return (
      <DashboardLayout title="Gender Report" subtitle="Loading…">
        <div className="text-sm text-[var(--vs-text-secondary)]">Loading gender breakdown…</div>
      </DashboardLayout>
    );
  }

  const t = data.totals;
  const pct = (n) => (t.total > 0 ? Math.round((n / t.total) * 100) : 0);

  return (
    <DashboardLayout
      title="Gender Report"
      subtitle="Boys / girls / other gender breakdown per hostel. Excludes alumni."
    >
      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <GenderCard icon={Users} label="Total Active" value={t.total} accent="border-[var(--vs-secondary)]" testId="gender-total-card" />
        <GenderCard icon={GenderMale} label="Boys" value={`${t.boys} (${pct(t.boys)}%)`} accent="border-[#3B82F6]" testId="gender-boys-card" />
        <GenderCard icon={GenderFemale} label="Girls" value={`${t.girls} (${pct(t.girls)}%)`} accent="border-[#EC4899]" testId="gender-girls-card" />
        <GenderCard icon={Question} label="Other / Unspecified" value={`${t.other} (${pct(t.other)}%)`} accent="border-[var(--vs-warning)]" testId="gender-other-card" />
      </div>

      {/* Per-hostel breakdown */}
      <div className="vs-card overflow-x-auto">
        <div className="p-4 border-b border-[var(--vs-border)]">
          <h3 className="font-semibold text-base">Per-hostel breakdown</h3>
          <p className="text-xs text-[var(--vs-text-secondary)] mt-1">Capacity vs occupancy is sourced from each hostel's `total_beds`.</p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">Hostel</th>
              <th className="px-4 py-3">Boys</th>
              <th className="px-4 py-3">Girls</th>
              <th className="px-4 py-3">Other</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Beds</th>
              <th className="px-4 py-3">Occupancy</th>
            </tr>
          </thead>
          <tbody data-testid="gender-rows">
            {data.rows.map((r) => {
              const occPct = r.total_beds ? Math.round((r.total / r.total_beds) * 100) : null;
              return (
                <tr key={r.hostel_id || "unallocated"} data-testid={`gender-row-${r.hostel_id || "unallocated"}`} className="border-t border-[var(--vs-border)]">
                  <td className="px-4 py-3 font-medium">
                    {r.hostel_name}
                    {!r.hostel_id && <Badge variant="outline" className="ml-2 text-[10px] border-[var(--vs-warning)]/40 text-[var(--vs-warning)]">unallocated</Badge>}
                  </td>
                  <td className="px-4 py-3 font-mono text-[#3B82F6] font-medium" data-testid={`row-boys-${r.hostel_id || "unallocated"}`}>{r.boys}</td>
                  <td className="px-4 py-3 font-mono text-[#EC4899] font-medium" data-testid={`row-girls-${r.hostel_id || "unallocated"}`}>{r.girls}</td>
                  <td className="px-4 py-3 font-mono text-[var(--vs-warning)] font-medium">{r.other}</td>
                  <td className="px-4 py-3 font-mono font-semibold">{r.total}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.total_beds ?? "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {occPct === null ? "—" : (
                      <Badge variant="outline" className={occupancyBadgeClass(occPct)}>{occPct}%</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
            {data.rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--vs-text-secondary)]">No data available.</td></tr>
            )}
          </tbody>
          {data.rows.length > 0 && (
            <tfoot className="bg-[var(--vs-bg-subtle)] border-t-2 border-[var(--vs-border)]">
              <tr className="font-semibold text-sm">
                <td className="px-4 py-3 uppercase font-mono text-[11px] tracking-widest text-[var(--vs-secondary)]">Total</td>
                <td className="px-4 py-3 font-mono text-[#3B82F6]">{t.boys}</td>
                <td className="px-4 py-3 font-mono text-[#EC4899]">{t.girls}</td>
                <td className="px-4 py-3 font-mono text-[var(--vs-warning)]">{t.other}</td>
                <td className="px-4 py-3 font-mono">{t.total}</td>
                <td className="px-4 py-3" colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </DashboardLayout>
  );
}
